import debounce from 'lodash/debounce';
import dropRight from 'lodash/dropRight';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import type { Map as YMap, Array as YArray, Text as YText, XmlFragment as YXmlFragment } from 'yjs';

export type UseObservedPropertyOptions<T> = {
  deepObserve: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selector?: (newData: any) => T;
};

export function useObservedProperty<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapToObserve: YMap<any>,
  propertyName?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: { deepObserve: boolean; selector: (newData: any) => T }
): [T, Dispatch<SetStateAction<T>>];

export function useObservedProperty<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapToObserve: YMap<any>,
  propertyName?: string,
  options?: { deepObserve: boolean }
): [T, Dispatch<SetStateAction<T>>];

export function useObservedProperty<T>(
  mapToObserve: YMap<T>,
  propertyName?: string,
  options: {
    deepObserve: boolean;
    selector?: (newData: unknown) => T;
  } = { deepObserve: false }
): [T, Dispatch<SetStateAction<T>>] {
  const { deepObserve, selector: selectorFn } = options;
  const selector = useCallback(
    (map: YMap<T>) => {
      if (!selectorFn && propertyName) {
        return map.get(propertyName) as T;
      }
      if (selectorFn && !propertyName) {
        return selectorFn(map);
      }
      if (selectorFn && propertyName) {
        return selectorFn(map.get(propertyName));
      }
      return map as T;
    },
    [selectorFn, propertyName]
  );

  const [value, setValue] = useState<T>(() => selector(mapToObserve));
  useEffect(() => {
    // Reinitialize when remounted
    const newValue = selector(mapToObserve);
    setValue(currentValue => {
      if (!isEqual(currentValue, newValue)) {
        return newValue;
      }
      return currentValue;
    });

    const handler = () => {
      const newValue = selector(mapToObserve);
      setValue(currentValue => {
        if (!isEqual(currentValue, newValue)) {
          return newValue;
        }
        return currentValue;
      });
    };

    if (deepObserve) {
      mapToObserve.observeDeep(handler);
      return () => mapToObserve.unobserveDeep(handler);
    } else {
      mapToObserve.observe(handler);
      return () => mapToObserve.unobserve(handler);
    }
  }, [mapToObserve, propertyName, deepObserve, selector]);

  const handleSetValue = useCallback(
    (newValue: T | ((prevState: T) => T)) => {
      if (!propertyName) {
        throw new Error('Cannot set value without property name');
      }
      if (newValue instanceof Function) {
        setValue(prevState => {
          const newState = newValue(prevState);
          const currentValue = mapToObserve.get(propertyName);
          if (!isEqual(currentValue, newState)) {
            mapToObserve.set(propertyName, newState);
          }
          return newState;
        });
      } else {
        setValue(newValue);
        const currentValue = mapToObserve.get(propertyName);
        if (!isEqual(currentValue, newValue)) {
          mapToObserve.set(propertyName, newValue);
        }
      }
    },
    [mapToObserve, propertyName]
  );

  return [value, handleSetValue];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useObservedArray<T>(mapToObserve: YMap<any>, propertyName: string) {
  const observedArray = mapToObserve.get(propertyName) as YArray<T>;
  const [value, setValue] = useState<T[]>(() => observedArray.toArray());

  useEffect(() => {
    // Reinitialize when remounted
    const newValue = observedArray.toArray();
    setValue(currentValue => (!isEqual(currentValue, newValue) ? newValue : currentValue));

    const handler = () => {
      const newValue = observedArray.toArray();
      setValue(currentValue => (!isEqual(currentValue, newValue) ? newValue : currentValue));
    };

    observedArray.observe(handler);
    return () => observedArray.unobserve(handler);
  }, [observedArray, propertyName]);

  return [value, observedArray] as const;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useObservedText(mapToObserve: YMap<any>, propertyName: string) {
  const observedText = mapToObserve.get(propertyName) as YText;
  const [value, setValue] = useState<string>(() => observedText.toString());

  useEffect(() => {
    // Reinitialize when remounted
    setValue(observedText.toString());

    const handler = () => {
      setValue(observedText.toString());
    };

    observedText.observe(handler);
    return () => observedText.unobserve(handler);
  }, [observedText, propertyName]);

  return [value, observedText] as const;
}

type useConvertObservedYXmlFragmentProps<T> = {
  fragment?: YXmlFragment;
  convertFunction: (v: YXmlFragment) => T;
};
export function useConvertObservedYXmlFragment<T>(props: useConvertObservedYXmlFragmentProps<T>) {
  const { fragment, convertFunction } = props;
  const [observeChange, setObserveChange] = useState(0);

  const result = useMemo(
    () => (fragment !== undefined ? convertFunction(fragment) : null),
    // observeChange is needed in dep array, but it's not being used in useMemo callback
    [fragment, convertFunction, observeChange]
  );

  useEffect(() => {
    const triggerObserveChange = debounce(() => {
      setObserveChange(prevState => prevState + 1);
    }, 300);

    fragment?.observeDeep(triggerObserveChange);
    return () => fragment?.unobserveDeep(triggerObserveChange);
  }, [fragment]);

  return result;
}

export function getNestedMap<T>(propertyPath: string, data: YMap<unknown> | undefined) {
  // Utility function to extract deeply nested properties
  // Example: answers.0.image.src for SelectionQuestion would look up root.contentObjects.<id>.answers.0.image.src
  // const { data } = useContentObject(parentMap);
  const propertyParts = propertyPath.split('.');
  const observedPropertyName = last(propertyParts) as string;
  const pathParts = dropRight(propertyParts);
  const nestedData: YMap<T> | undefined = pathParts.reduce(
    (data, pathPart) => data?.get(pathPart),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data as YMap<any> | undefined
  );
  if (!nestedData) {
    throw new Error(`Nested map at property path ${propertyPath} not found.`);
  }
  return [observedPropertyName, nestedData] as const;
}

export function useObservedNestedProperty<T>(propertyPath: string, data: YMap<unknown> | undefined) {
  const [observedPropertyName, nestedData] = getNestedMap(propertyPath, data);
  return useObservedProperty<T>(nestedData, observedPropertyName);
}
